import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-comparison-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

type CompAge = '25' | '30' | '35' | '40' | '45' | '50';
type CompCoverage = '250k' | '500k' | '1m';
type CompType = 'term20' | 'term30' | 'whole' | 'final';

interface CompResult {
  carrier: string;
  amBest: string;
  type: string;
  monthlyRate: number;
  rateClass: string;
  approvalTime: string;
  bestFor: string;
  badge?: string;
}

const COMP_BASE: Record<CompAge, Record<CompCoverage, Record<CompType, number>>> = {
  '25': { '250k': { term20: 14, term30: 18, whole: 145, final: 0 }, '500k': { term20: 23, term30: 30, whole: 285, final: 0 }, '1m': { term20: 39, term30: 51, whole: 565, final: 0 } },
  '30': { '250k': { term20: 19, term30: 25, whole: 175, final: 0 }, '500k': { term20: 28, term30: 38, whole: 345, final: 0 }, '1m': { term20: 48, term30: 65, whole: 685, final: 0 } },
  '35': { '250k': { term20: 24, term30: 32, whole: 215, final: 0 }, '500k': { term20: 36, term30: 49, whole: 425, final: 0 }, '1m': { term20: 64, term30: 87, whole: 845, final: 0 } },
  '40': { '250k': { term20: 36, term30: 49, whole: 275, final: 0 }, '500k': { term20: 55, term30: 75, whole: 545, final: 0 }, '1m': { term20: 97, term30: 132, whole: 1085, final: 0 } },
  '45': { '250k': { term20: 57, term30: 78, whole: 355, final: 0 }, '500k': { term20: 88, term30: 120, whole: 705, final: 0 }, '1m': { term20: 153, term30: 209, whole: 1405, final: 0 } },
  '50': { '250k': { term20: 90, term30: 0, whole: 455, final: 52 }, '500k': { term20: 139, term30: 0, whole: 905, final: 92 }, '1m': { term20: 245, term30: 0, whole: 1805, final: 0 } },
};

const POLICY_TYPES: { value: CompType; label: string; desc: string; icon: string }[] = [
  { value: 'term20', label: '20-Year Term', desc: 'Cheapest option — fixed rate for 20 years', icon: 'ri-calendar-check-line' },
  { value: 'term30', label: '30-Year Term', desc: 'Maximum term protection — fixed for 30 years', icon: 'ri-calendar-2-line' },
  { value: 'whole', label: 'Whole Life', desc: 'Permanent coverage + cash value growth', icon: 'ri-shield-check-line' },
  { value: 'final', label: 'Final Expense', desc: 'Ages 50–85 · $5K–$25K · No exam', icon: 'ri-heart-line' },
];

function getCompResults(age: CompAge, coverage: CompCoverage, type: CompType): CompResult[] {
  const base = COMP_BASE[age][coverage][type];
  if (base === 0) return [];

  if (type === 'term20' || type === 'term30') {
    return [
      { carrier: 'Banner Life', amBest: 'A+', type: type === 'term20' ? '20-Year Term' : '30-Year Term', monthlyRate: base, rateClass: 'Preferred Plus', approvalTime: '24–48 hrs', bestFor: 'Lowest rates in Indianapolis', badge: 'Lowest Rate' },
      { carrier: 'Protective Life', amBest: 'A+', type: type === 'term20' ? '20-Year Term' : '30-Year Term', monthlyRate: Math.round(base * 1.04), rateClass: 'Preferred Plus', approvalTime: '24–72 hrs', bestFor: 'Best conversion rights' },
      { carrier: 'Pacific Life', amBest: 'A+', type: type === 'term20' ? '20-Year Term' : '30-Year Term', monthlyRate: Math.round(base * 1.07), rateClass: 'Preferred Plus', approvalTime: '1–2 weeks', bestFor: 'Best for $500K+ coverage' },
      { carrier: 'Prudential', amBest: 'A+', type: type === 'term20' ? '20-Year Term' : '30-Year Term', monthlyRate: Math.round(base * 1.10), rateClass: 'Preferred', approvalTime: '1–3 weeks', bestFor: 'Best for health conditions' },
      { carrier: 'Mutual of Omaha', amBest: 'A+', type: type === 'term20' ? '20-Year Term' : '30-Year Term', monthlyRate: Math.round(base * 1.12), rateClass: 'Preferred', approvalTime: '1–2 weeks', bestFor: 'Best child rider options' },
    ];
  }

  if (type === 'whole') {
    return [
      { carrier: 'Northwestern Mutual', amBest: 'A++', type: 'Whole Life', monthlyRate: base, rateClass: 'Preferred Plus', approvalTime: '2–4 weeks', bestFor: 'Highest dividends — 165+ consecutive years', badge: 'Best Whole Life' },
      { carrier: 'MassMutual', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(base * 0.97), rateClass: 'Preferred Plus', approvalTime: '2–4 weeks', bestFor: 'Strong dividends + competitive premiums' },
      { carrier: 'New York Life', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(base * 0.95), rateClass: 'Preferred Plus', approvalTime: '2–4 weeks', bestFor: 'Largest mutual insurer in the US' },
      { carrier: 'Guardian Life', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(base * 0.93), rateClass: 'Preferred Plus', approvalTime: '2–4 weeks', bestFor: 'Best for Indianapolis business owners' },
      { carrier: 'Mutual of Omaha', amBest: 'A+', type: 'Whole Life', monthlyRate: Math.round(base * 0.88), rateClass: 'Preferred Plus', approvalTime: '1–2 weeks', bestFor: 'Most affordable whole life in Indianapolis' },
    ];
  }

  return [
    { carrier: 'Mutual of Omaha', amBest: 'A+', type: 'Final Expense', monthlyRate: base, rateClass: 'Simplified Issue', approvalTime: '24–48 hrs', bestFor: 'Best final expense carrier — return of premium', badge: 'Best Final Expense' },
    { carrier: 'AIG / Corebridge', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(base * 1.05), rateClass: 'Simplified Issue', approvalTime: '24–48 hrs', bestFor: 'Strong second option for Indianapolis seniors' },
    { carrier: 'Gerber Life', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(base * 1.08), rateClass: 'Simplified Issue', approvalTime: '24–48 hrs', bestFor: 'Well-known brand — familiar to Indianapolis seniors' },
    { carrier: 'Transamerica', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(base * 1.12), rateClass: 'Simplified Issue', approvalTime: '24–48 hrs', bestFor: 'Good for Indianapolis seniors with some health history' },
    { carrier: 'Colonial Penn', amBest: 'A-', type: 'Final Expense', monthlyRate: Math.round(base * 1.15), rateClass: 'Guaranteed Issue', approvalTime: '1–2 weeks', bestFor: 'Guaranteed approval — no health questions' },
  ];
}

const POLICY_COMPARISON = [
  { factor: 'Monthly Cost ($500K, age 35)', term20: '$36/mo', term30: '$49/mo', whole: '$425/mo', final: 'N/A' },
  { factor: 'Coverage Duration', term20: '20 years', term30: '30 years', whole: 'Lifetime', final: 'Lifetime' },
  { factor: 'Coverage Amount', term20: 'Up to $5M+', term30: 'Up to $5M+', whole: 'Up to $5M+', final: '$5K–$25K' },
  { factor: 'Cash Value', term20: 'No', term30: 'No', whole: 'Yes — grows tax-deferred', final: 'Small' },
  { factor: 'Medical Exam', term20: 'Often no', term30: 'Often no', whole: 'Usually yes', final: 'No' },
  { factor: 'Rate Locked In', term20: 'Yes — 20 years', term30: 'Yes — 30 years', whole: 'Yes — lifetime', final: 'Yes — lifetime' },
  { factor: 'Best For', term20: 'Income replacement, mortgage', term30: 'Young families, 30-yr mortgage', whole: 'Estate planning, wealth building', final: 'Seniors, final expenses' },
];

const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-shield-check-line' },
  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis', icon: 'ri-heart-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
];

const FAQS = [
  {
    q: 'What is the best type of life insurance in Indianapolis?',
    a: 'The best type of life insurance in Indianapolis depends on your specific needs and goals. Term life insurance is best for: Most Indianapolis families who need income replacement and mortgage protection. It\'s 80–90% cheaper than whole life for the same death benefit. A healthy 35-year-old can get $500K 20-year term for $36/month. Whole life insurance is best for: Indianapolis residents with estate planning needs, business owners who need key person insurance, or those who want permanent coverage with cash value growth. Final expense insurance is best for: Indianapolis seniors ages 50–85 who need $5,000–$25,000 to cover funeral and burial costs. No medical exam required. The bottom line: for most Indianapolis families, term life insurance provides the best value. An independent agent can help you determine which type is right for your specific situation.',
  },
  {
    q: 'How do I compare life insurance carriers in Indianapolis?',
    a: 'When comparing life insurance carriers in Indianapolis, look at these key factors: 1. AM Best financial strength rating: Stick with A or A+ rated carriers — they\'re financially strong and will be around to pay your claim. 2. Monthly premium: Compare rates for the same coverage amount and term length. 3. Rate class: Preferred Plus, Preferred, Standard Plus, Standard — your health determines your rate class. 4. Underwriting guidelines: Different carriers have different rules for health conditions. Some are more lenient with specific conditions. 5. Policy features: Conversion rights, accelerated death benefit rider, waiver of premium rider. 6. Approval time: Accelerated underwriting (24–48 hours) vs. traditional underwriting (2–4 weeks). The best way to compare: work with an independent agent who can shop all available carriers simultaneously. Call (317) 648-2983 for a free carrier comparison.',
  },
  {
    q: 'What is the difference between term and whole life insurance in Indianapolis?',
    a: 'Term life insurance vs. whole life insurance in Indianapolis: Term life insurance: Covers you for a specific period (10, 20, or 30 years). If you die during the term, your beneficiary receives the death benefit. If you outlive the term, coverage ends. Much cheaper — a 35-year-old can get $500K 20-year term for $36/month. Best for income replacement and mortgage protection. Whole life insurance: Covers you for your entire lifetime. Builds cash value that grows tax-deferred. Premiums are fixed for life. Much more expensive — a 35-year-old pays $425/month for $500K whole life. Best for estate planning, wealth building, and permanent coverage needs. Which is right for you? For most Indianapolis families, term life insurance provides the best value. Whole life makes sense for specific estate planning or wealth building goals. An independent agent can help you determine which is right for your situation.',
  },
  {
    q: 'Which life insurance carrier has the best rates in Indianapolis?',
    a: 'The life insurance carrier with the best rates in Indianapolis depends on your age, health, and coverage needs. For term life insurance: Banner Life and Protective Life consistently offer the lowest term life rates in Indianapolis. For a healthy 35-year-old, $500K 20-year term: Banner Life: $36/month. Protective Life: $37/month. Pacific Life: $39/month. For whole life insurance: Guardian Life and Mutual of Omaha typically offer the most competitive whole life rates in Indianapolis. For final expense insurance: Mutual of Omaha is consistently the best carrier for Indianapolis seniors. The key: rates vary significantly by carrier for the same coverage. An independent agent compares all available carriers to find the lowest rate for your specific age, health, and coverage needs. Call (317) 648-2983 for a free carrier comparison.',
  },
  {
    q: 'How much does life insurance cost in Indianapolis?',
    a: 'Life insurance costs in Indianapolis vary significantly by type, age, coverage amount, and health. Here are sample monthly rates for a healthy non-smoker: 20-Year Term Life: Age 25, $500K: $23/month. Age 30, $500K: $28/month. Age 35, $500K: $36/month. Age 40, $500K: $55/month. Age 45, $500K: $88/month. Age 50, $500K: $139/month. Whole Life: Age 35, $500K: $425/month. Age 40, $500K: $545/month. Final Expense: Age 65, $10K: $68/month. Age 70, $10K: $92/month. These are Preferred Plus rates — the lowest available rate class. Most Indianapolis residents qualify for Preferred or Standard rates, which are slightly higher. An independent agent can get your exact personalized rate from all available carriers.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Michael & Sarah T.',
    location: 'Carmel, Indianapolis',
    quote: 'We didn\'t know whether to get term or whole life. Benjamin walked us through the comparison and we got $1M 20-year term for $75/month each. Perfect for our stage of life.',
    stars: 5,
    coverage: '$1M each, 20-year term',
    rate: '$75/month each at age 35',
  },
  {
    name: 'Robert K.',
    location: 'Fishers, Indianapolis',
    quote: 'I was comparing carriers online and getting confused. Benjamin compared 12 carriers in 20 minutes and found me Banner Life at $36/month for $500K. Saved me hours of research.',
    stars: 5,
    coverage: '$500K 20-year term',
    rate: '$36/month at age 35',
  },
  {
    name: 'Dorothy M.',
    location: 'Indianapolis, IN',
    quote: 'Age 68, needed final expense coverage. Benjamin compared 5 carriers and found Mutual of Omaha at $68/month for $10K. No health questions, approved in 15 minutes.',
    stars: 5,
    coverage: '$10K final expense',
    rate: '$68/month at age 68',
  },
  {
    name: 'James & Carol W.',
    location: 'Noblesville, Indianapolis',
    quote: 'We wanted whole life for estate planning. Benjamin compared Northwestern Mutual, MassMutual, and Guardian and found us the best combination of dividends and premiums.',
    stars: 5,
    coverage: '$500K whole life each',
    rate: 'Best whole life rates found',
  },
];

export default function LifeInsuranceComparisonIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [compAge, setCompAge] = useState<CompAge>('35');
  const [compCoverage, setCompCoverage] = useState<CompCoverage>('500k');
  const [compType, setCompType] = useState<CompType>('term20');
  const [results, setResults] = useState<CompResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance Comparison Indianapolis IN | Compare All Types & Carriers | Hoosier Life Insurance';
    const description = 'Compare life insurance in Indianapolis, IN. Term vs. whole vs. final expense. 5 top carriers compared side by side. Find the best policy for your Indianapolis family. Call (317) 648-2983.';
    const keywords = 'life insurance comparison Indianapolis, compare life insurance Indianapolis, best life insurance Indianapolis, life insurance types Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/0ba8ddd94b699f8473d9fc24eb97dd9e.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance Comparison Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: CompAge[] = ['25', '30', '35', '40', '45', '50'];
  const coverageOptions: { value: CompCoverage; label: string }[] = [
    { value: '250k', label: '$250K' }, { value: '500k', label: '$500K' }, { value: '1m', label: '$1M' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance Comparison</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20life%20insurance%20comparison%20chart%20analysis%20modern%20office%20warm%20natural%20light%20financial%20planning%20advisor%20family%20protection%20photorealistic%20warm%20neutral%20tones%20cream%20beige%20wide%20angle&width=1440&height=700&seq=comp-li-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance Comparison Indianapolis Indiana — Compare All Types and Carriers"
            title="Life Insurance Comparison in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-scales-3-line text-sm"></i>
              Indianapolis &amp; Marion County · Term vs. Whole vs. Final Expense · 5 Carriers Compared
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Compare Life Insurance Carriers &amp; Rates —{' '}
              <span className="text-[#D97642]">Indianapolis, IN</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Compare all types of life insurance in Indianapolis — term, whole life, and final expense — side by side. Use our <strong className="text-white">Policy Comparison Tool</strong> to find the best policy and carrier for your specific needs.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '4', label: 'Policy types compared — term, whole life, final expense, and more' },
                { value: '5', label: 'Top carriers compared side by side for Indianapolis applicants' },
                { value: '$36/mo', label: 'Lowest term life rate for a healthy 35-year-old in Indianapolis' },
                { value: 'Free', label: 'Carrier comparison — no obligation, no pressure' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#comparison-tool" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-scales-3-line text-lg"></i>
                Compare My Options
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '4', label: 'Policy types compared — term, whole life, final expense, and universal life' },
              { value: '5', label: 'Top carriers compared side by side for Indianapolis applicants' },
              { value: '80%', label: 'Savings when choosing term life over whole life for the same death benefit' },
              { value: 'Free', label: 'Independent carrier comparison — no obligation, no pressure' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* Policy Type Comparison Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Policy Type Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Term vs. Whole Life vs. Final Expense — Indianapolis
              </h2>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="bg-[#1A3A52]">
                      <th className="px-5 py-3 text-left text-xs font-bold text-white/70 uppercase tracking-wide">Factor</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">20-Yr Term</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">30-Yr Term</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">Whole Life</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">Final Expense</th>
                    </tr>
                  </thead>
                  <tbody>
                    {POLICY_COMPARISON.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-5 py-4 text-sm font-semibold text-[#1A3A52]">{row.factor}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.term20}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.term30}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.whole}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.final}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* COMPARISON TOOL */}
            <div id="comparison-tool" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Policy Comparison Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Life Insurance Carriers — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and policy type to see the top 5 carriers ranked for Indianapolis applicants.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-scales-3-line text-[#D97642]"></i>
                    Your Comparison Profile
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setCompAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${compAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setCompCoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${compCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Policy Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {POLICY_TYPES.map(opt => (
                        <button key={opt.value} onClick={() => { setCompType(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${compType === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <i className={`${opt.icon} text-sm`}></i>
                            <span className="text-sm font-bold">{opt.label}</span>
                          </div>
                          <div className={`text-xs ${compType === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getCompResults(compAge, compCoverage, compType))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-scales-3-line text-lg"></i>
                    Compare My Options
                  </button>
                </div>

                {results !== null && results.length > 0 && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        {POLICY_TYPES.find(t => t.value === compType)?.label} — Age {compAge}
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${Math.min(...results.map(r => r.monthlyRate))}/mo
                      </span>
                    </div>
                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">Best for: {r.bestFor}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-400">Rate class: {r.rateClass}</span>
                                  <span className="text-xs text-gray-400">Approval: {r.approvalTime}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${(r.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results !== null && results.length === 0 && (
                  <div className="px-7 py-8 text-center">
                    <p className="text-sm text-gray-500">This combination is not available. Please try a different age or coverage amount.</p>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-scales-3-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and policy type above, then click <strong className="text-[#1A3A52]">Compare My Options</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Families Who Found the Right Policy
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.rate}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance Comparison Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-comp-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-scales-3-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Carrier Comparison</h3>
                    <p className="text-xs text-white/70">20+ carriers · All policy types</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Comparison Call</h3>
                    <p className="text-xs text-white/70">All policy types · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Comparison Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Policy type questions or coverage needs? (Optional)',
                    submitButton: 'Confirm My Free Comparison Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Find the right life insurance policy for your Indianapolis family
          </h2>
          <p className="text-base text-white/75 mb-7">
            Term, whole life, or final expense — use our comparison tool to find the best policy and carrier for your specific needs. Free, no obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#comparison-tool" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-scales-3-line text-lg"></i>
              Compare My Options
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={184} productName="Life Insurance Comparison Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Life Insurance Comparison" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Life Insurance Comparison — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
